import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CardModule } from '@openng/optimus-ui/card';
import { SelectModule } from '@openng/optimus-ui/select';
import { TableModule } from '@openng/optimus-ui/table';
import { TagModule } from '@openng/optimus-ui/tag';
import {
  PracticeCourseAggregateFreshness,
  PracticeCourseFilters,
  PracticeCourseSummary,
} from '../../models/practice-course.types';
import { PracticeCoursesService } from '../../services/practice-courses.service';

@Component({
  selector: 'app-practice-courses',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    SelectModule,
    TableModule,
    TagModule,
  ],
  templateUrl: './practice-courses.component.html',
  styleUrl: './practice-courses.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PracticeCoursesComponent {
  private readonly service = inject(PracticeCoursesService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected filters = signal<PracticeCourseFilters | null>(null);
  protected usersTruncated = signal(false);
  protected courses = signal<PracticeCourseSummary[]>([]);
  protected total = signal(0);
  protected loading = signal(true);
  protected error = signal<string | null>(null);
  protected freshness = signal<PracticeCourseAggregateFreshness | null>(null);
  protected first = signal(0);

  protected readonly selectedOrg = signal<{ id: number; name: string } | null>(null);
  protected readonly selectedCourse = signal<PracticeCourseFilters['courses'][number] | null>(null);
  protected readonly selectedTeam = signal<PracticeCourseFilters['teams'][number] | null>(null);
  protected readonly selectedUser = signal<PracticeCourseFilters['users'][number] | null>(null);
  protected selectedReadiness: string | null = null;
  protected selectedAssignmentStatus: string | null = null;
  protected selectedEnrollmentStatus: string | null = null;
  protected startDate = '';
  protected endDate = '';

  protected courseOptions = computed(() =>
    (this.filters()?.courses ?? []).filter(
      (course) => !this.selectedOrg() || course.organisationId === this.selectedOrg()!.id,
    ),
  );
  protected teamOptions = computed(() =>
    (this.filters()?.teams ?? []).filter(
      (team) => !this.selectedOrg() || team.organisationId === this.selectedOrg()!.id,
    ),
  );
  protected userOptions = computed(() =>
    (this.filters()?.users ?? []).filter(
      (user) => !this.selectedOrg() || user.organisationId === this.selectedOrg()!.id,
    ),
  );

  /** One request stream for the list: a newer load (page change, filter change) cancels the
   * in-flight request instead of racing it, so a stale response can never overwrite the newer one. */
  private readonly loadRequest$ = new Subject<
    { first?: number | null; rows?: number | null } | undefined
  >();

  /** Debounced user-search terms from the user filter. `p-select`'s built-in filter only searches
   * the already-loaded 500 users, so a typed term goes to the API (`searchUsersByTerm`, cap 1000)
   * and the results are merged into the options. */
  private readonly userSearch$ = new Subject<string>();

  constructor() {
    this.service
      .getFilters()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (filters) => {
          this.filters.set(filters);
          this.usersTruncated.set(filters.usersTruncated ?? false);
        },
        error: (error) => this.error.set(error.message),
      });
    this.userSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        // catchError inside the switchMap: a failed search must not terminate the stream, or every
        // later keystroke would emit into a dead Subject. A failed search keeps the current list.
        switchMap((term) =>
          term.trim().length === 0
            ? of(null)
            : this.service.getFilters(term.trim()).pipe(catchError(() => of(null))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (search) => {
          if (!search) return;
          // Merge in the server-side matches; keep the rest of the filter state untouched.
          this.filters.update((filters) =>
            filters ? { ...filters, users: search.users } : filters,
          );
        },
      });
    this.loadRequest$
      .pipe(
        // catchError lives inside the switchMap: an error reaching the root subscriber would
        // terminate the Subject permanently, and the "Try again" button pushes into it.
        switchMap((event) =>
          this.fetchCourses(event).pipe(
            catchError((error) => {
              this.error.set(error.message);
              this.loading.set(false);
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          if (!response) return;
          this.courses.set(response.data);
          this.total.set(response.total);
          this.freshness.set(response.freshness);
          this.loading.set(false);
        },
      });
    this.load();
  }

  private fetchCourses(event?: { first?: number | null; rows?: number | null }) {
    const rows = event?.rows ?? 20;
    const page = Math.floor((event?.first ?? 0) / rows);
    this.loading.set(true);
    this.error.set(null);
    return this.service.getCourses({
      organisationId: this.selectedOrg()?.id,
      courseVersionId: this.selectedCourse()?.id,
      teamId: this.selectedTeam()?.id,
      userId: this.selectedUser()?.id,
      readinessState: this.selectedReadiness ?? undefined,
      assignmentStatus: this.selectedAssignmentStatus ?? undefined,
      enrollmentStatus: this.selectedEnrollmentStatus ?? undefined,
      startDate: this.startDate ? new Date(`${this.startDate}T00:00:00Z`) : undefined,
      endDate: this.endDate ? new Date(`${this.endDate}T23:59:59.999Z`) : undefined,
      page,
      pageSize: rows,
    });
  }

  protected load(event?: { first?: number | null; rows?: number | null }) {
    this.loadRequest$.next(event);
  }

  protected applyFilters() {
    this.first.set(0);
    this.load();
  }

  /** `p-select` emits `{ filter }` as the user types in the filter input. */
  protected onUserFilter(event: { filter?: string }) {
    this.userSearch$.next(event.filter ?? '');
  }

  protected onOrgChange(org: { id: number; name: string } | null) {
    this.selectedOrg.set(org);
    if (this.selectedCourse()?.organisationId !== org?.id) this.selectedCourse.set(null);
    if (this.selectedTeam()?.organisationId !== org?.id) this.selectedTeam.set(null);
    if (this.selectedUser()?.organisationId !== org?.id) this.selectedUser.set(null);
    this.applyFilters();
  }

  protected clearFilters() {
    this.selectedOrg.set(null);
    this.selectedCourse.set(null);
    this.selectedTeam.set(null);
    this.selectedUser.set(null);
    this.selectedReadiness = null;
    this.selectedAssignmentStatus = null;
    this.selectedEnrollmentStatus = null;
    this.startDate = '';
    this.endDate = '';
    this.applyFilters();
  }

  protected retry() {
    this.load({ first: this.first(), rows: 20 });
  }

  protected open(course: PracticeCourseSummary) {
    this.router.navigate(['/practice-courses', course.id]);
  }

  protected userName(user: PracticeCourseFilters['users'][number]) {
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email;
  }

  protected severity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'PUBLISHED' || status === 'ACTIVE' || status === 'CERTIFIED') return 'success';
    if (status === 'BLOCKED' || status === 'CANCELED') return 'danger';
    if (status === 'ARCHIVED') return 'secondary';
    return 'info';
  }
}
